import { doc, getDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { authenticateParticipant } from './surveyRepository';
import { sessionKey, validateCheckpoint, fail, clone } from '../learning/sessionModel';

export async function openLearningSession(lesson, participantId, mode, storage = window.localStorage) {
  if (!participantId.trim() || participantId.length > 80) throw fail('session/identity', 'Enter the participant ID your teacher gave you.');
  if (!['demo', 'research'].includes(mode) || (mode === 'research' && !lesson.research_ready)) throw fail('session/not-ready', 'This lesson is not ready for research use.');
  // Demo previews never authenticate or write to Firebase, even with a configured project.
  const owner = mode === 'demo' ? `demo-${participantId}` : (await authenticateParticipant()).uid;
  const id = sessionKey(lesson, owner);
  const draftKey = `learning-pending:${mode}:${id}`;
  const demoKey = `learning-demo:${id}`;
  const reference = mode === 'research' ? doc(db, 'lesson_sessions', owner, 'sessions', id) : null;
  const readDemo = () => JSON.parse(storage.getItem(demoKey) || 'null');
  const stored = mode === 'demo' ? readDemo()?.checkpoint : await getDoc(reference).then(snapshot => snapshot.exists() ? snapshot.data() : null);
  if (stored) validateCheckpoint(stored, lesson, participantId, owner, mode);
  const pending = JSON.parse(storage.getItem(draftKey) || 'null');
  if (pending) validateCheckpoint(pending.checkpoint, lesson, participantId, owner, mode);
  return {
    sessionId: id, owner, checkpoint: stored || null, pending,
    stagePending(operation) { storage.setItem(draftKey, JSON.stringify(operation)); },
    clearPending() { storage.removeItem(draftKey); },
    async commit(operation) {
      const { checkpoint, base_revision: revision, operation_id: operationId, events } = operation;
      validateCheckpoint(checkpoint, lesson, participantId, owner, mode);
      const next = { ...clone(checkpoint), revision: revision + 1, last_operation_id: operationId };
      function checkCurrent(current) {
        if (current?.last_operation_id === operationId) return 'already-saved';
        if ((current?.revision || 0) !== revision) throw fail('session/conflict', 'This session changed in another tab. Load the saved progress before continuing.');
        if (current?.status === 'completed') throw fail('session/completed', 'This session is already complete.');
        return 'save';
      }
      if (mode === 'demo') {
        const current = readDemo();
        if (checkCurrent(current?.checkpoint) === 'save') storage.setItem(demoKey, JSON.stringify({ checkpoint: next, events: [...(current?.events || []), ...events] }));
      } else {
        if (auth.currentUser?.uid !== owner) throw fail('session/unauthorized', 'Your sign-in changed. Reload this page.');
        // Snapshot remains compact; event batches are immutable subcollection documents.
        await runTransaction(db, async transaction => {
          const snapshot = await transaction.get(reference);
          if (checkCurrent(snapshot.exists() ? snapshot.data() : null) === 'already-saved') return;
          transaction.set(reference, { ...next, timestamp: serverTimestamp() });
          for (let index = 0; index < events.length; index += 40) {
            transaction.set(doc(reference, 'events', `${operationId}-${index / 40}`), {
              owner_uid: owner, study_id: lesson.study_id, lesson_id: lesson.lesson_id,
              lesson_version: lesson.lesson_version, session_id: id, data_mode: 'research',
              operation_id: operationId, revision: revision + 1,
              events: events.slice(index, index + 40), timestamp: serverTimestamp()
            });
          }
        });
      }
      // A cleanup failure cannot turn a successful remote save into a failed save.
      try { storage.removeItem(draftKey); } catch (error) { /* An idempotent retry will clear it later. */ }
      return next;
    }
  };
}

export function learningError(error) {
  if (error.code === 'permission-denied') return 'Saving is not allowed yet. Ask your teacher to check the lesson security rules.';
  if (error.code === 'auth/operation-not-allowed' || error.code === 'auth/configuration-not-found') return 'Sign-in is not set up yet. Ask your teacher for help.';
  if (error.name === 'QuotaExceededError' || error.name === 'SecurityError') return 'This device cannot store your progress. Ask your teacher for help before closing the page.';
  if (error.code?.startsWith('session/')) return error.message;
  return 'We could not save yet. Keep this page open and try again.';
}
